import { computed, Injectable, signal, effect } from '@angular/core';
import { Product, ProductFilters, TotalProductsSoldType } from 'src/app/models/core/product.model';
import { ProductApiService } from './product-api.service';
import { EventSellerService } from '../event/event-seller.service';
import { TicketStateService } from '../ticket/ticket-state.service';
import { PaymentType } from 'src/app/types/constants';
import { forkJoin } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DirectSaleProductService {
  /* ────────────────────────────  Signals base  ──────────────────────────── */
  // Productos separados por tipo de pago
  readonly productsCash = signal<Product[]>([]);
  readonly productsPayAtDoor = signal<Product[]>([]);

  // Contadores por tipo de recaudación
  readonly cashCount = signal(0);
  readonly payAtDoorCount = signal(0);

  // Paginación por tipo
  readonly cashCurrentPage = signal(1);
  readonly payAtDoorCurrentPage = signal(1);
  readonly cashLastPage = signal(1);
  readonly payAtDoorLastPage = signal(1);

  // Estados de carga por tipo
  readonly loadingCash = signal(false);
  readonly loadingPayAtDoor = signal(false);
  readonly loadingMoreCash = signal(false);
  readonly loadingMorePayAtDoor = signal(false);

  // Estados generales
  readonly totalPages = signal(1);
  readonly configs = signal<any>(null);
  readonly isLoading = signal(false);
  readonly loadingMore = signal(false);
  readonly partialLoading = signal(false);
  readonly currentPage = signal(1);
  readonly searchTerm = signal('');


  readonly loadingCategories = signal(false);
  readonly selectedCategoryId = signal<number>(-1);
  public categories = signal<{ id: number; name: string }[]>([]);

  // Nuevo signal para product type
  private _selectedProductType = signal<string>('all');
  readonly selectedProductType = computed(() => this._selectedProductType());

  private _selectedPaymentType = signal<PaymentType>('all');
  readonly selectedPaymentType = computed(() => this._selectedPaymentType());

  readonly filters = signal<ProductFilters>({});

  readonly hasActiveFilters = computed(() => {
    const f = this.filters();
    const hasCategoryFilter = !!f.category && f.category !== '-1';
    const hasProductTypeFilter = this.selectedProductType() !== 'all';
    return hasCategoryFilter || hasProductTypeFilter;
  });

  readonly productsLoaded = signal(0);

  // Computed para obtener productos según el tipo seleccionado
  readonly products = computed(() => {
    const paymentType = this.selectedPaymentType();
    if (paymentType === 'cash') {
      return this.productsCash();
    } else if (paymentType === 'pay_at_door') {
      return this.productsPayAtDoor();
    }
    return [...this.productsCash(), ...this.productsPayAtDoor()];
  });

  // Computed para obtener el estado de carga según el tipo seleccionado
  readonly isLoadingByType = computed(() => {
    const paymentType = this.selectedPaymentType();
    if (paymentType === 'cash') {
      return this.loadingCash();
    } else if (paymentType === 'pay_at_door') {
      return this.loadingPayAtDoor();
    }
    return this.loadingCash() || this.loadingPayAtDoor();
  });

  // Computed para obtener el estado de carga más productos según el tipo seleccionado
  readonly isLoadingMoreByType = computed(() => {
    const paymentType = this.selectedPaymentType();
    if (paymentType === 'cash') {
      return this.loadingMoreCash();
    } else if (paymentType === 'pay_at_door') {
      return this.loadingMorePayAtDoor();
    }
    return this.loadingMoreCash() || this.loadingMorePayAtDoor();
  });

  readonly totalProductsSoldType = computed<TotalProductsSoldType>(() => {
    return this.configs()?.total_products_sold_type ?? 'percentage';
  });

  setSelectedPaymentType(type: PaymentType) {
    this._selectedPaymentType.set(type);
    // No longer triggers API calls - just updates the computed products
  }

  setSearchTerm(term: string) {
    this.searchTerm.set(term);
    this.currentPage.set(1);
    this.productsCash.set([]);
    this.productsPayAtDoor.set([]);
    this.totalPages.set(1);

    if (term && term.trim().length > 0) {
      this.loadingCash.set(true);
      this.loadingPayAtDoor.set(true);
    }
  }

  setCategoryFilter(categoryId: number) {
    this.selectedCategoryId.set(categoryId);
    this.filters.set({
      ...this.filters(),
      category: categoryId?.toString() || undefined
    });

    this.loadingCash.set(true);
    this.loadingPayAtDoor.set(true);
  }

  setSelectedProductType(type: string) {
    this._selectedProductType.set(type);
    this.filters.set({
      ...this.filters(),
      product_type: type !== 'all' ? type : undefined
    });

    this.loadingCash.set(true);
    this.loadingPayAtDoor.set(true);
  }

  /**
   * Emits an event when the products have been loaded
   * so the component can select the correct tab
   */
  private emitProductsLoaded() {
    this.productsLoaded.set(this.productsLoaded() + 1);
  }

  /**
   * Determina qué tab seleccionar basado en los productos disponibles
   * @returns 'left' | 'right' - El tab que debe seleccionarse
   */
  getRecommendedTab(): 'left' | 'right' {
    const cashProducts = this.productsCash();
    const payAtDoorProducts = this.productsPayAtDoor();

    const hasCashProducts = cashProducts.length > 0;
    const hasPayAtDoorProducts = payAtDoorProducts.length > 0;

    if (hasCashProducts && hasPayAtDoorProducts) {
      return 'left';
    }

    if (!hasCashProducts && hasPayAtDoorProducts) {
      return 'right';
    }
    return 'left';
  }

  /* ────────────────────────────  Signals externos  ─────────────────────── */
  readonly event = computed(() => this.eventSeller.event());

  constructor(
    private api: ProductApiService,
    private eventSeller: EventSellerService,
    private ticketStateService: TicketStateService,
  ) {
    // Effect to listen when a ticket is generated and refresh products
    effect(() => {
      const shouldRefresh = this.ticketStateService.shouldRefreshProducts();
      const updatedProductId = this.ticketStateService.updatedProductId();

      if (shouldRefresh && updatedProductId) {
        // Reload products to reflect changes
        const eventId = this.event()?.id;
        if (eventId) {
          this.refreshProductsAsync(eventId);
        }
      }
    });
  }

  loadProductsByType(eventId: number, type: 'cash' | 'pay_at_door', page = 1, categoryId?: number, productType?: string, loadType: 'full' | 'load-more' = 'full') {
    const perPage = 10;
    const searchTerm = this.searchTerm();

    if (loadType === 'full') {
      if (type === 'cash') {
        this.loadingCash.set(true);
        this.cashCurrentPage.set(page);
      } else {
        this.loadingPayAtDoor.set(true);
        this.payAtDoorCurrentPage.set(page);
      }
    } else {
      if (type === 'cash') {
        this.loadingMoreCash.set(true);
      } else {
        this.loadingMorePayAtDoor.set(true);
      }
    }

    this.api.getAllSellerProducts(eventId, page, perPage, categoryId, type, productType, searchTerm).subscribe({
      next: ({ data, meta, configs }) => {
        if (loadType === 'full') {
          if (type === 'cash') {
            this.productsCash.set(data);
            this.cashCurrentPage.set(page);
            this.cashLastPage.set(Math.ceil(meta.total / perPage));
            this.cashCount.set(meta.total);
            this.loadingCash.set(false);
          } else {
            this.productsPayAtDoor.set(data);
            this.payAtDoorCurrentPage.set(page);
            this.payAtDoorLastPage.set(Math.ceil(meta.total / perPage));
            this.payAtDoorCount.set(meta.total);
            this.loadingPayAtDoor.set(false);
          }
        } else {
          if (type === 'cash') {
            const currentProducts = this.productsCash();
            this.productsCash.set([...currentProducts, ...data]);
            this.cashCurrentPage.set(page);
            this.loadingMoreCash.set(false);
          } else {
            const currentProducts = this.productsPayAtDoor();
            this.productsPayAtDoor.set([...currentProducts, ...data]);
            this.payAtDoorCurrentPage.set(page);
            this.loadingMorePayAtDoor.set(false);
          }
        }

        this.configs.set(configs ?? null);
      },
      error: err => {
        console.error(`Error al cargar productos ${type}`, err);
        if (type === 'cash') {
          this.loadingCash.set(false);
          this.loadingMoreCash.set(false);
        } else {
          this.loadingPayAtDoor.set(false);
          this.loadingMorePayAtDoor.set(false);
        }
      }
    });
  }

  loadInitialProductsByType(eventId: number, categoryId?: number, productType?: string, type: 'full' | 'partial' = 'full') {
    const hasSearchTerm = this.searchTerm() && this.searchTerm().trim().length > 0;
    const hasCategoryFilter = categoryId !== undefined && categoryId !== -1;
    const hasProductTypeFilter = productType !== undefined && productType !== 'all';

    // Determine loading state based on whether we have data
    const hasData = this.productsCash().length > 0 || this.productsPayAtDoor().length > 0;
    const shouldShowFullLoading = type === 'full' && !hasData;
    const shouldShowFilterLoading = hasCategoryFilter || hasProductTypeFilter || hasSearchTerm;

    if (shouldShowFullLoading || shouldShowFilterLoading) {
      this.loadingCash.set(true);
      this.loadingPayAtDoor.set(true);
    }

    this.isLoading.set(shouldShowFullLoading);
    this.loadingMore.set(type === 'partial' && hasData);

    const cash$ = this.api.getAllSellerProducts(eventId, 1, 10, categoryId, 'cash', productType, this.searchTerm());
    const payAtDoor$ = this.api.getAllSellerProducts(eventId, 1, 10, categoryId, 'pay_at_door', productType, this.searchTerm());

    forkJoin([cash$, payAtDoor$]).subscribe({
      next: ([cashRes, payAtDoorRes]) => {
        this.productsCash.set(cashRes.data);
        this.cashCurrentPage.set(1);
        this.cashLastPage.set(Math.ceil(cashRes.meta.total / 10));
        this.cashCount.set(cashRes.meta.total);

        this.productsPayAtDoor.set(payAtDoorRes.data);
        this.payAtDoorCurrentPage.set(1);
        this.payAtDoorLastPage.set(Math.ceil(payAtDoorRes.meta.total / 10));
        this.payAtDoorCount.set(payAtDoorRes.meta.total);

        this.configs.set(cashRes.configs ?? payAtDoorRes.configs ?? null);
        this.isLoading.set(false);
        this.loadingMore.set(false);
        this.loadingCash.set(false);
        this.loadingPayAtDoor.set(false);

        this.emitProductsLoaded();
      },
      error: err => {
        console.error('Error al cargar productos por tipo', err);
        this.isLoading.set(false);
        this.loadingMore.set(false);
        this.partialLoading.set(false);
        this.loadingCash.set(false);
        this.loadingPayAtDoor.set(false);
      }
    });
  }

  loadMoreProductsByType(eventId: number, type: 'cash' | 'pay_at_door', categoryId?: number, productType?: string) {
    const currentPage = type === 'cash' ? this.cashCurrentPage() : this.payAtDoorCurrentPage();
    const lastPage = type === 'cash' ? this.cashLastPage() : this.payAtDoorLastPage();
    const nextPage = currentPage + 1;

    if (nextPage <= lastPage) {
      this.loadProductsByType(eventId, type, nextPage, categoryId, productType, 'load-more');
    }
  }

  // Legacy method for compatibility
  loadProducts(eventId: number, page = 1, categoryId?: number, productType?: string, type: 'full' | 'load-more' = 'full') {
    const paymentType = this.selectedPaymentType();
    if (paymentType === 'all') {
      this.loadInitialProductsByType(eventId, categoryId, productType, type === 'full' ? 'full' : 'partial');
    } else {
      this.loadProductsByType(eventId, paymentType, page, categoryId, productType, type);
    }
  }

  loadMoreProducts(eventId: number, categoryId?: number, productType?: string) {
    const paymentType = this.selectedPaymentType();
    if (paymentType === 'all') {
      this.loadMoreProductsByType(eventId, 'cash', categoryId, productType);
      this.loadMoreProductsByType(eventId, 'pay_at_door', categoryId, productType);
    } else {
      this.loadMoreProductsByType(eventId, paymentType, categoryId, productType);
    }
  }

  /**
   * Reloads products after a ticket is generated
   * Keeps current filters but updates data
   */
  private refreshProductsAfterTicketGeneration(eventId: number) {
    const categoryId = this.selectedCategoryId() !== -1 ? this.selectedCategoryId() : undefined;
    const productType = this.selectedProductType();
    const updatedProductId = this.ticketStateService.updatedProductId();

    if (updatedProductId) {
      const cashProduct = this.productsCash().find(p => p.id === updatedProductId);
      const payAtDoorProduct = this.productsPayAtDoor().find(p => p.id === updatedProductId);

      if (cashProduct) {
        this.loadProductsByType(eventId, 'cash', 1, categoryId ?? undefined, productType, 'full');
      } else if (payAtDoorProduct) {
        this.loadProductsByType(eventId, 'pay_at_door', 1, categoryId ?? undefined, productType, 'full');
      }
    }
  }

  /**
   * Updates products asynchronously to avoid conflicts with effects
   */
  private async refreshProductsAsync(eventId: number) {
    // Wait for the next event cycle
    await new Promise<void>(resolve => queueMicrotask(() => resolve()));
    this.refreshProductsAfterTicketGeneration(eventId);
  }

  /**
   * Gets the payment type of the updated product
   */
  getUpdatedProductPaymentType(): 'cash' | 'pay_at_door' | null {
    const updatedProductId = this.ticketStateService.updatedProductId();
    if (!updatedProductId) return null;

    const cashProduct = this.productsCash().find(p => p.id === updatedProductId);
    if (cashProduct) return 'cash';

    const payAtDoorProduct = this.productsPayAtDoor().find(p => p.id === updatedProductId);
    if (payAtDoorProduct) return 'pay_at_door';

    return null;
  }


  loadCategories(eventId: number) {
    this.loadingCategories.set(true);

    this.api.getCategoriesBySeller(eventId, true).subscribe({
      next: (response) => {
        this.categories.set([
          { id: -1, name: 'Todos los productos' },
          ...response.data,
        ]);
        this.selectedCategoryId.set(this.categories()[0].id);
        this.loadingCategories.set(false);
      },
      error: () => {
        this.loadingCategories.set(false);
        console.error('Error loading categories');
      }
    });
  }

  loadAll(eventId: number): void {
    if (!this.event() || this.event()!.id !== eventId) {
      this.eventSeller.loadEvent(eventId);
    }
    this.loadInitialProductsByType(eventId, undefined, undefined, 'full');
    this.loadCategories(eventId);
  }

  reset() {
    this.productsCash.set([]);
    this.productsPayAtDoor.set([]);
    this.cashCount.set(0);
    this.payAtDoorCount.set(0);
    this.cashCurrentPage.set(1);
    this.payAtDoorCurrentPage.set(1);
    this.cashLastPage.set(1);
    this.payAtDoorLastPage.set(1);
    this.loadingCash.set(false);
    this.loadingPayAtDoor.set(false);
    this.loadingMoreCash.set(false);
    this.loadingMorePayAtDoor.set(false);
    this.totalPages.set(1);
    this.configs.set(null);
    this.isLoading.set(false);
    this.loadingMore.set(false);
    this.currentPage.set(1);
    this.selectedCategoryId.set(-1);
    this._selectedProductType.set('all');
    this.filters.set({});
  }

}
